/**
 * Milestone Status Definitions
 * Complete Excel-specification-compliant status configurations
 * Extracted from milestoneConfigEnhanced.ts for better modularity
 */

export interface EnhancedMilestoneStatus {
  id: string;
  label: string;
  // Shown instead of `label` while emailSent is not yet true for this status
  // (i.e. SEND_FOR_APPROVAL stage in utils/approvalFlow.ts, including right
  // after a reopen, which resets emailSent to false). Falls back to `label`.
  pendingLabel?: string;
  excelRowReference?: number; // For traceability to Excel specifications

  // CELL DISPLAY DATA (Columns D-J from Excel)
  cellColor: string;
  textColor: string;
  icon?: string;
  cellDisplayText?: string;
  cellDateFormat?: string;
  cellTimeFormat?: string;
  cellSecondaryDateFormat?: string;
  cellAdditionalText?: string;
  striped?: boolean;

  // POPUP/ROLLOVER DATA (Columns after J from Excel)
  rolloverCopy: string;
  popupDateFormat?: string;
  popupTimeFormat?: string;
  popupSecondaryDateFormat?: string;
  popupAdditionalFields?: string;
  showSecondaryDate?: boolean;
  secondaryDateLabel?: string;
  showApprover?: boolean;

  // EDIT SCREEN DATA - Enhanced for Excel compliance
  editFieldRequirements?: {
    required: string[];
    optional: string[];
    hidden: string[];
    conditional: { [key: string]: string[] };
  };

  // Enhanced validation rules
  validationRules?: {
    [fieldId: string]: {
      type: 'required' | 'optional' | 'conditional';
      dependsOn?: string;
      values?: string[];
      customValidator?: (value: any, allData: any) => boolean;
    };
  };

  // Calendar field restrictions - controls what date selection types are available
  calendarFieldRestrictions?: {
    [fieldId: string]: {
      hideMonthList?: boolean; // Hide month row in calendar
      hideQuarterList?: boolean; // Hide quarter row in calendar
      hideYearList?: boolean; // Hide year row in calendar
    };
  };

  // Date relationship validation rules
  dateRelationshipRules?: {
    [fieldId: string]: {
      relationshipType:
        | 'greater_than'
        | 'less_than'
        | 'greater_than_or_equal'
        | 'less_than_or_equal';
      compareToField: string;
      errorMessage: string;
      allowSameDate?: boolean;
    };
  };

  // Historical data handling
  historicalFields?: {
    approverLabel?: string;
    dateLabel?: string;
    showHistoricalData?: boolean;
    preserveOriginalData?: boolean;
  };

  // Progressive field additions
  progressiveFields?: string[];
  isSystemDriven?: boolean;
}

// Mirrors the SEND_FOR_APPROVAL vs PENDING split in utils/approvalFlow.ts's
// computeApprovalFlowStage: emailSent true -> label (e.g. "Sent"); otherwise
// -> pendingLabel (e.g. "Send for Review"), falling back to label.
export const getMilestoneStatusLabel = (
  status?: EnhancedMilestoneStatus,
  emailSent?: boolean
): string | undefined =>
  emailSent ? status?.label : status?.pendingLabel || status?.label;

export const ENHANCED_MILESTONE_STATUSES: Record<
  string,
  EnhancedMilestoneStatus
> = {
  // Row 1: Target (Meeting)
  TARGET: {
    id: 'target',
    label: 'Target',
    excelRowReference: 1,
    cellColor: '#FFFFFF',
    textColor: '#000000',
    // icon: 'none',
    cellDateFormat: '[Mon] or [Mon XX]',
    cellTimeFormat: 'n/a',
    showApprover: true, // Show approver in rollover for L1/L2 approval-required milestones
    rolloverCopy: 'Meeting Target',
    popupDateFormat: '[Mon] or [Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: true,
    editFieldRequirements: {
      required: ['type_of_review'],
      optional: ['document_link', 'recording_link', 'notes_link'],
      hidden: ['status', 'date', 'approver'],
      conditional: {}
    }
  },

  // Row 2: Scheduled (Meeting) - Keep original key for backward compatibility
  MEETING_SCHEDULED: {
    id: 'meeting_scheduled',
    label: 'Scheduled',
    excelRowReference: 2,
    cellColor: '#FFFFFF',
    textColor: '#000000',
    icon: 'calendar',
    cellDateFormat: '[Mon XX]',
    cellTimeFormat: 'at [11:11AM - 12:12PM UTC]',
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    rolloverCopy: 'Meeting Scheduled on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'at [11:11AM - 12:12PM UTC]',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'meeting_scheduled_at'],
      optional: [
        'meeting_link',
        'notes_link',
        'document_link',
        'recording_link'
      ],
      hidden: ['approver'],
      conditional: {}
    },
    validationRules: {
      meeting_scheduled_at: {
        type: 'required',
        customValidator: (value, allData) => {
          // Validate time format
          return /^\d{1,2}:\d{2}(AM|PM) - \d{1,2}:\d{2}(AM|PM) (UTC|GMT|[A-Z]{2,4})$/.test(
            value
          );
        }
      }
    },
    // Calendar restrictions for scheduled status
    // Disable month and quarter selection for date field (which is the meeting date for scheduled status)
    // Only allow specific date selection since we need a precise date for scheduling
    calendarFieldRestrictions: {
      date: {
        hideMonthList: true,
        hideQuarterList: true
      },
      meeting_scheduled_on: {
        hideMonthList: true,
        hideQuarterList: true
      }
    }
  },

  // Row 3: Not Approved - To be Approved Async (Meeting)
  NOT_APPROVED_TO_BE_APPROVED_ASYNC: {
    id: 'not_approved_to_be_approved_async',
    label: 'Not Approved - To be Approved Async',
    excelRowReference: 3,
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'envelope',
    cellDateFormat: '[Mon XX]',
    cellTimeFormat: 'n/a',
    cellDisplayText: 'Follow-up',
    cellSecondaryDateFormat: '[Mon XX]', // Show secondary date (async target) in cell
    rolloverCopy: 'Not Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    popupSecondaryDateFormat: '[Mon XX]',
    showSecondaryDate: true, // Show for both cell and popup
    secondaryDateLabel: 'Async Target Date',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'secondary_date',
        'approver'
      ],
      optional: [
        'meeting_link',
        'notes_link',
        'document_link',
        'recording_link'
      ],
      hidden: [],
      conditional: {}
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'NOT APPROVED ON',
      showHistoricalData: true
    },
    dateRelationshipRules: {
      secondary_date: {
        relationshipType: 'greater_than',
        compareToField: 'date',
        errorMessage: 'Async Target Date must be after the Not Approved date',
        allowSameDate: false
      }
    }
  },

  // Row 4: Replanning (Meeting) - For Approval Required scenarios
  REPLANNING_APPROVAL_REQUIRED: {
    id: 'replanning_approval_required',
    label: 'Replanning',
    excelRowReference: 4,
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle',
    cellDateFormat: '[Mon XX]',
    cellTimeFormat: 'n/a',
    rolloverCopy: 'Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'approver'],
      optional: [
        'meeting_link',
        'notes_link',
        'document_link',
        'recording_link'
      ],
      hidden: [],
      conditional: {}
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'APPROVED ON',
      showHistoricalData: true
    }
  },

  // Row 4: Replanning (Meeting) - For No Approval Required scenarios
  REPLANNING_NO_APPROVAL: {
    id: 'replanning_no_approval',
    label: 'Replanning',
    excelRowReference: 4,
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle',
    cellDateFormat: '[Mon XX]',
    cellTimeFormat: 'n/a',
    showApprover: true, // Show approver in rollover for L1/L2 approval-required milestones
    rolloverCopy: 'Originally Completed on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date'],
      optional: [
        'meeting_link',
        'notes_link',
        'document_link',
        'recording_link'
      ],
      hidden: ['approver'],
      conditional: {}
    }
  },

  // Keep original REPLANNING for backward compatibility
  REPLANNING: {
    id: 'replanning',
    label: 'Replanning',
    excelRowReference: 4,
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'arrow-circle',
    cellDateFormat: '[Mon XX]',
    cellTimeFormat: 'n/a',
    showApprover: true, // Show approver in rollover for L1/L2 approval-required milestones
    rolloverCopy: 'Approved on', // Default to approval required behavior
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date'],
      optional: [
        'meeting_link',
        'notes_link',
        'document_link',
        'recording_link'
      ],
      hidden: ['approver'],
      conditional: {}
    }
  },

  // Row 4: Not Approved (Meeting)
  NOT_APPROVED: {
    id: 'not_approved',
    label: 'Not Approved',
    excelRowReference: 4,
    cellColor: '#fbeaea', // Red background (standardized)
    textColor: '#000000',
    icon: '',
    cellDisplayText: '',
    cellDateFormat: '[Mon XX]', // Remove the plain text display - only show pill
    rolloverCopy: 'Not Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'at [11:11AM - 12:12PM PST]',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'approver'],
      optional: ['recording_link', 'notes_link', 'document_link'],
      hidden: [],
      conditional: {}
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'NOT APPROVED ON',
      showHistoricalData: true,
      preserveOriginalData: true
    },
    progressiveFields: ['recording_link', 'notes_link']
  },

  // Row 5: Not Approved (Async)
  NOT_APPROVED_ASYNC: {
    id: 'not_approved_async',
    label: 'Not Approved', // Removed '(Async)' suffix for cleaner user display
    excelRowReference: 5,
    cellColor: '#fbeaea', // Red background
    textColor: '#000000', // Black text
    icon: 'envelope',
    cellDisplayText: '', // Remove the plain text display - only show pill
    cellDateFormat: '[Mon XX]',
    rolloverCopy: 'Not Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'approver'],
      optional: ['document_link', 'notes_link', 'recording_link'],
      hidden: [],
      conditional: {}
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'NOT APPROVED ON',
      showHistoricalData: true
    }
  },

  // Row 6: Approved (Meeting)
  APPROVED: {
    id: 'approved',
    label: 'Approved',
    excelRowReference: 6,
    cellColor: '#F3FFEE',
    textColor: '#000000',
    icon: '',
    cellDisplayText: '', // Remove the plain text display - only show pill
    cellDateFormat: '[Mon XX]',
    rolloverCopy: 'Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'at [11:11AM - 12:12PM PST]',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'approver',
        'document_link'
      ],
      optional: ['recording_link', 'notes_link'],
      hidden: [],
      conditional: {}
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'APPROVED ON',
      showHistoricalData: true
    },
    progressiveFields: ['document_link', 'recording_link', 'notes_link']
  },

  // Row 7: Approved (Async)
  APPROVED_ASYNC: {
    id: 'approved_async',
    label: 'Approved', // Removed '(Async)' suffix for cleaner user display
    excelRowReference: 7,
    cellColor: '#F3FFEE',
    textColor: '#000000',
    icon: 'envelope',
    cellDisplayText: '', // Remove the plain text display - only show pill
    cellDateFormat: '[Mon XX]',
    rolloverCopy: 'Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'approver',
        'document_link'
      ],
      optional: ['notes_link', 'recording_link', 'notes_link', 'document_link'],
      hidden: [],
      conditional: {}
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'APPROVED ON',
      showHistoricalData: true
    },
    progressiveFields: ['document_link', 'notes_link']
  },

  // Row 8: Re-Approved (Meeting)
  RE_APPROVED: {
    id: 're_approved',
    label: 'Re-Approved',
    excelRowReference: 8,
    cellColor: '#F3FFEE', // Standardized green color with Approved
    textColor: '#000000',
    icon: 'arrow_circle',
    cellDisplayText: '', // Remove the plain text display - only show pill
    cellSecondaryDateFormat: '[Mon XX]',
    rolloverCopy: 'Originally Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    popupSecondaryDateFormat: '[Mon XX]',
    showSecondaryDate: true,
    secondaryDateLabel: 'Re-Approval Date',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'secondary_date',
        'approver',
        'document_link'
      ],
      optional: ['recording_link', 'notes_link'],
      hidden: [],
      conditional: {}
    },
    dateRelationshipRules: {
      secondary_date: {
        relationshipType: 'greater_than',
        compareToField: 'date',
        errorMessage:
          'Re-Approval Date must be after the Original Approval date',
        allowSameDate: false
      }
    },
    historicalFields: {
      showHistoricalData: true,
      approverLabel: 'APPROVER',
      dateLabel: 'APPROVED ON',
      preserveOriginalData: true
    }
  },

  // Row 9: Re-Approved (Async)
  RE_APPROVED_ASYNC: {
    id: 're_approved_async',
    label: 'Re-Approved', // Removed '(Async)' suffix for cleaner user display
    excelRowReference: 9,
    cellColor: '#F3FFEE', // Standardized green color with Approved
    textColor: '#000000',
    icon: 'arrow_circle',
    cellDisplayText: '', // Remove the plain text display - only show pill
    cellSecondaryDateFormat: '[Mon XX]',
    rolloverCopy: 'Originally Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    popupSecondaryDateFormat: '[Mon XX]',
    showSecondaryDate: true,
    secondaryDateLabel: 'Re-Approval Date',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'secondary_date',
        'approver',
        'document_link'
      ],
      optional: ['recording_link', 'notes_link'],
      hidden: [],
      conditional: {}
    },
    dateRelationshipRules: {
      secondary_date: {
        relationshipType: 'greater_than',
        compareToField: 'date',
        errorMessage:
          'Re-Approval Date must be after the Original Approval date',
        allowSameDate: false
      }
    },
    historicalFields: {
      showHistoricalData: true,
      approverLabel: 'APPROVER',
      dateLabel: 'APPROVED ON',
      preserveOriginalData: true
    }
  },

  // Row 10: Complete (Meeting)
  COMPLETE: {
    id: 'complete',
    label: 'Complete',
    excelRowReference: 10,
    cellColor: '#F3FFEE', // Standardized green color with Approved
    textColor: '#000000', // Black text
    icon: '',
    cellDateFormat: '[Mon XX]',
    cellDisplayText: '', // Remove the plain text display - only show pill
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    rolloverCopy: 'Completed on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'document_link'],
      optional: ['recording_link', 'notes_link'],
      hidden: ['approver'], // Hide approver for NO Approval Required scenarios
      conditional: {}
    }
  },

  // Row 11: Approved - Needs Async Follow-Up (Meeting) - NEW STATUS
  APPROVED_FOLLOW_UP: {
    id: 'approved_follow_up',
    label: 'Approved - Needs Async Follow-Up',
    excelRowReference: 11,
    cellColor: '#F3FFEE',
    textColor: '#D31510',
    icon: 'envelope',
    cellDisplayText: 'Follow-up',
    cellDateFormat: 'n/a', // Don't show approval date in cell, only secondary date
    cellTimeFormat: 'n/a',
    cellSecondaryDateFormat: '[Mon XX]',
    rolloverCopy: 'Approved on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    popupSecondaryDateFormat: '[Mon XX]',
    showSecondaryDate: true,
    secondaryDateLabel: 'Follow-up Target Date',
    showApprover: true,
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'secondary_date',
        'approver',
        'document_link'
      ],
      optional: ['recording_link', 'notes_link'],
      hidden: [],
      conditional: {}
    },
    dateRelationshipRules: {
      secondary_date: {
        relationshipType: 'greater_than',
        compareToField: 'date',
        errorMessage: 'Follow-up Target Date must be after the Approval date',
        allowSameDate: false
      }
    },
    historicalFields: {
      approverLabel: 'APPROVER',
      dateLabel: 'APPROVED ON',
      showHistoricalData: true
    },
    progressiveFields: ['document_link', 'recording_link', 'notes_link']
  },

  // Row 12: Send Target (Async)
  ASYNC_TARGET: {
    id: 'async_target',
    label: 'Target', // Changed from 'Send Target' to 'Target' as requested
    excelRowReference: 12,
    cellColor: '#FFFFFF', // Changed to white background as requested
    textColor: '#000000',
    icon: 'envelope',
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    rolloverCopy: 'Send Target',
    cellDateFormat: '[Mon] or [Mon XX]',
    popupDateFormat: '[Mon] or [Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false, // Changed to false so users can edit the target date
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date'], // Include date as required for target date
      optional: ['document_link', 'recording_link', 'notes_link'],
      hidden: ['approver'], // Hide approver but show date and status
      conditional: {}
    }
  },

  // Row 13: Sent (Async)
  SENT: {
    id: 'sent',
    label: 'Sent',
    pendingLabel: 'Send for Review',
    excelRowReference: 13,
    cellColor: '#FFFCE0',
    textColor: '#000000',
    icon: 'envelope',
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    cellDisplayText: 'Due on',
    cellDateFormat: '[Mon XX]', // Show the primary date (when sent) in the cell
    cellSecondaryDateFormat: '[Mon XX]', // Show secondary date (due date) in cell
    rolloverCopy: 'Sent on',
    popupDateFormat: '[Mon XX, YYYY]', // Changed to match Excel expectation
    popupTimeFormat: 'n/a',
    popupSecondaryDateFormat: '[Mon XX, YYYY]', // Changed to match Excel expectation
    showSecondaryDate: true, // Show for both cell and popup
    secondaryDateLabel: 'Due Date',
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'secondary_date',
        'document_link'
      ],
      optional: ['recording_link', 'notes_link'],
      hidden: ['approver'],
      conditional: {}
    },
    dateRelationshipRules: {
      secondary_date: {
        relationshipType: 'greater_than',
        compareToField: 'date',
        errorMessage: 'Due Date must be after the Sent date',
        allowSameDate: false
      }
    }
  },

  // Row 14: Sent - Pending (Async)
  SENT_PENDING: {
    id: 'sent_pending',
    label: 'Sent - Feedback Pending', // Changed from 'Sent - Pending' to 'Sent - Feedback Pending' as requested
    excelRowReference: 14,
    cellColor: '#FFFCE0',
    textColor: '#000000',
    icon: 'envelope',
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    cellDisplayText: 'Pending',
    cellDateFormat: '[Mon XX]', // Show the sent date in the cell
    cellSecondaryDateFormat: '[Mon XX]', // Show secondary date (pending until) in cell
    rolloverCopy: 'Sent on',
    popupDateFormat: '[Mon XX, YYYY]', // Changed to match Excel expectation
    popupTimeFormat: 'n/a',
    popupSecondaryDateFormat: '[Mon XX, YYYY]', // Changed to match Excel expectation
    showSecondaryDate: true, // Show for both cell and popup
    secondaryDateLabel: 'Pending Until Date',
    isSystemDriven: false,
    editFieldRequirements: {
      required: [
        'type_of_review',
        'status',
        'date',
        'secondary_date',
        'document_link'
      ],
      optional: ['recording_link', 'notes_link'],
      hidden: ['approver'],
      conditional: {}
    },
    dateRelationshipRules: {
      secondary_date: {
        relationshipType: 'greater_than',
        compareToField: 'date',
        errorMessage: 'Pending Until Date must be after the Sent date',
        allowSameDate: false
      }
    }
  },

  // Row 15: Async Pending (Async)
  ASYNC_PENDING: {
    id: 'async_pending',
    label: 'Pending',
    excelRowReference: 15,
    cellColor: '#FFFCE0',
    textColor: '#F57C00',
    icon: 'clock',
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    cellDisplayText: '', // Remove the plain text display - only show pill
    rolloverCopy: 'Async Pending on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date'],
      optional: ['document_link', 'recording_link', 'notes_link'],
      hidden: ['approver'],
      conditional: {}
    }
  },

  // Row 16: Complete (Async)
  COMPLETE_ASYNC: {
    id: 'complete_async',
    label: 'Complete (Async)',
    excelRowReference: 16,
    cellColor: '#F3FFEE', // Standardized green color with Approved
    textColor: '#000000', // Black text
    icon: 'complete',
    showApprover: true, // Show approver in rollover for L1/L2 when any status is set
    cellDateFormat: '[Mon XX]',
    cellDisplayText: '', // Remove the plain text display - only show pill
    rolloverCopy: 'Complete on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'document_link'],
      optional: ['recording_link', 'notes_link'],
      hidden: ['approver'], // Hide approver for NO Approval Required scenarios
      conditional: {}
    }
  },

  // Row 17: TBD (Meeting/Async)
  TBD: {
    id: 'tbd',
    label: 'TBD',
    excelRowReference: 17,
    cellColor: '#FFFFFF', // White background as requested
    textColor: '#EA3829', // Red text from Figma (Alias/content/visual/semantic/negative)
    // icon: 'none',
    cellDisplayText: 'TBD',
    rolloverCopy: 'TBD',
    popupDateFormat: 'TBD',
    popupTimeFormat: 'n/a',
    isSystemDriven: true,
    editFieldRequirements: {
      required: ['type_of_review'],
      optional: ['notes_link'], // Add notes_link for TBD scenarios
      hidden: ['status', 'date', 'approver', 'document_link', 'recording_link'],
      conditional: {}
    }
  },

  // Row 18: Not Required (Meeting)
  NOT_REQUIRED_MEETING: {
    id: 'not_required_meeting',
    label: '',
    excelRowReference: 18,
    cellColor: '#F3FFEE',
    textColor: '#666666',
    // icon: 'none',
    cellDisplayText: 'Not Required',
    rolloverCopy: 'Not Required',
    popupDateFormat: 'n/a',
    popupTimeFormat: 'n/a',
    isSystemDriven: true,
    striped: true,
    editFieldRequirements: {
      required: ['type_of_review'],
      optional: [], // No link fields for Not Required
      hidden: ['status', 'date', 'approver', 'document_link', 'recording_link'],
      conditional: {}
    }
  },

  // Row 19: Optional (Meeting)
  OPTIONAL_MEETING: {
    id: 'optional_meeting',
    label: 'Optional',
    excelRowReference: 19,
    cellColor: '#E2EDE0',
    textColor: '#666666',
    // icon: 'none',
    rolloverCopy: 'Optional',
    popupDateFormat: 'n/a',
    popupTimeFormat: 'n/a',
    isSystemDriven: true,
    striped: true,
    editFieldRequirements: {
      required: ['type_of_review'],
      optional: [], // No link fields for Optional
      hidden: ['status', 'date', 'approver', 'document_link', 'recording_link'],
      conditional: {}
    }
  },

  // Row 20: Sent (Async)
  SENT_ASYNC: {
    id: 'sent_async',
    label: 'Sent (Async)',
    excelRowReference: 20,
    cellColor: '#FFFCE0', // Yellow background (standardized with other Sent statuses)
    textColor: '#1976D2',
    icon: 'send',
    cellDisplayText: '', // Remove the plain text display - only show pill
    showApprover: true, // Show approver in rollover for L1/L2 approval-required milestones
    rolloverCopy: 'Sent on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date'],
      optional: ['document_link', 'recording_link', 'notes_link'],
      hidden: ['approver'],
      conditional: {}
    }
  },

  // Row 21: Complete (Async) - Duplicate of row 16, using different ID
  COMPLETE_ASYNC_ALT: {
    id: 'complete_async_alt',
    label: 'Complete (Async)',
    excelRowReference: 21,
    cellColor: '#F3FFEE', // Standardized green color with Approved
    textColor: '#000000', // Black text
    icon: 'complete',
    cellDateFormat: '[Mon XX]',
    cellDisplayText: '', // Remove the plain text display - only show pill
    showApprover: true, // Show approver in rollover for L1/L2 approval-required milestones
    rolloverCopy: 'Complete on',
    popupDateFormat: '[Mon XX]',
    popupTimeFormat: 'n/a',
    isSystemDriven: false,
    editFieldRequirements: {
      required: ['type_of_review', 'status', 'date', 'document_link'],
      optional: ['recording_link', 'notes_link'],
      hidden: ['approver'],
      conditional: {}
    }
  },

  // Row 22: Not Required (Async)
  NOT_REQUIRED: {
    id: 'not_required',
    label: '',
    excelRowReference: 22,
    cellColor: '#F3FFEE',
    textColor: '#666666',
    // icon: 'none',
    cellDisplayText: 'Not Required', // ✅ Add cellDisplayText for copy functionality
    rolloverCopy: 'Not Required',
    popupDateFormat: 'n/a',
    popupTimeFormat: 'n/a',
    isSystemDriven: true,
    striped: true,
    editFieldRequirements: {
      required: ['type_of_review'],
      optional: [], // No link fields for Not Required
      hidden: ['status', 'date', 'approver', 'document_link', 'recording_link'],
      conditional: {}
    }
  },

  // Row 23: Optional (Async)
  OPTIONAL: {
    id: 'optional',
    label: 'Optional',
    excelRowReference: 23,
    cellColor: '#E2EDE0',
    textColor: '#666666',
    // icon: 'none',
    rolloverCopy: 'Optional',
    popupDateFormat: 'n/a',
    popupTimeFormat: 'n/a',
    isSystemDriven: true,
    striped: true,
    editFieldRequirements: {
      required: ['type_of_review'],
      optional: [], // No link fields for Optional
      hidden: ['status', 'date', 'approver', 'document_link', 'recording_link'],
      conditional: {}
    }
  }

  // Not Needed (same as Not Required but separate ID for "Not Needed" review type)
  // NOT_NEEDED: {
  //   id: 'not_needed',
  //   label: 'Not Needed',
  //   excelRowReference: 22, // Same as NOT_REQUIRED
  //   cellColor: '#F3FFEE',
  //   textColor: '#666666',
  //   // icon: 'none',
  //   cellDisplayText: 'Not Needed',
  //   rolloverCopy: 'Not Needed',
  //   popupDateFormat: 'n/a',
  //   popupTimeFormat: 'n/a',
  //   isSystemDriven: true,
  //   striped: true,
  //   editFieldRequirements: {
  //     required: ['type_of_review'],
  //     optional: [], // No link fields for Not Needed
  //     hidden: ['status', 'date', 'approver', 'document_link', 'recording_link'],
  //     conditional: {}
  //   }
  // }
};

// Enhanced status menus based on Approval Condition + Type of Review combinations

// Enhanced Status Menus - Define available statuses for each review type
export const ENHANCED_STATUS_MENUS: Record<string, string[]> = {
  // Both | TBD
  TBD: ['TBD'],

  // This is used for Approval Required | Meeting
  Meeting: [
    'TARGET',
    'MEETING_SCHEDULED', // Scheduled
    'NOT_APPROVED',
    'NOT_APPROVED_TO_BE_APPROVED_ASYNC',
    'APPROVED',
    'APPROVED_FOLLOW_UP', // Approved - Needs Async Follow-Up
    'REPLANNING_APPROVAL_REQUIRED', // Use approval-required variant
    'RE_APPROVED'
  ],

  // This is used for Approval Required | Async
  Async: [
    'ASYNC_TARGET', // Target
    'SENT',
    'NOT_APPROVED_ASYNC', // Not Approved
    'APPROVED_ASYNC', // Approved
    'REPLANNING_APPROVAL_REQUIRED', // Use approval-required variant
    'RE_APPROVED_ASYNC' // Re-Approved
  ],

  // NO Approval Required | Meeting
  'No Approval Required Meeting': [
    'TARGET',
    'MEETING_SCHEDULED', // Scheduled
    'COMPLETE',
    'REPLANNING_NO_APPROVAL' // Use no-approval variant
  ],

  // NO Approval Required | Async
  'No Approval Required Async': [
    'ASYNC_TARGET', // Target
    'SENT',
    'COMPLETE',
    'REPLANNING_NO_APPROVAL' // Use no-approval variant
  ]
};
